
import React, { useState, useMemo, useEffect, useRef } from 'react';
import { User, ResidentUnit, SentMessage, UserRole, UnitType, Attachment } from '../types';
import { MOCK_RESIDENTS, MOCK_USERS, MOCK_TICKETS } from '../mockData';
import { 
  Send, 
  Users, 
  Search, 
  CheckCircle2, 
  History, 
  Mail, 
  Smartphone, 
  X, 
  Loader2, 
  Bell, 
  Check, 
  FileText, 
  Download, 
  Share2, 
  PieChart, 
  BarChart3, 
  Shield, 
  Paperclip, 
  Video, 
  Play, 
  UserMinus 
} from 'lucide-react';

interface CommunicationProps {
  user: User;
  initialRecipientId?: string | null;
  onMessageSent: () => void;
}

interface ReportTemplate {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  category: 'FINANCE' | 'MAINTENANCE' | 'RESIDENTS';
}

const Communication: React.FC<CommunicationProps> = ({ user, initialRecipientId, onMessageSent }) => {
  const isAdmin = user.role === UserRole.PRESIDENT || user.role === UserRole.DEPUTY || user.role === UserRole.SUPER_ADMIN;
  
  const [activeView, setActiveView] = useState<'NEW' | 'HISTORY' | 'REPORTS'>(isAdmin ? 'NEW' : 'NEW');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [sentHistory, setSentHistory] = useState<SentMessage[]>([]);
  const [isSending, setIsSending] = useState(false);
  const [showReportPreview, setShowReportPreview] = useState<ReportTemplate | null>(null);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  
  const fileInputRef = useRef<HTMLInputElement>(null);

  const reportTemplates: ReportTemplate[] = [
    { id: 'rep1', title: 'Месечен Финансиски Извештај', description: 'Преглед на наплата и тековни трошоци.', icon: <PieChart className="w-6 h-6" />, category: 'FINANCE' },
    { id: 'rep2', title: 'Статус на Одржување', description: 'Преглед на сите дефекти (активни/решени).', icon: <BarChart3 className="w-6 h-6" />, category: 'MAINTENANCE' },
    { id: 'rep3', title: 'Регистар на Станари', description: 'Листа на сите сопственици и закупувачи.', icon: <Users className="w-6 h-6" />, category: 'RESIDENTS' },
  ];

  useEffect(() => {
    if (initialRecipientId) {
      setSelectedIds(new Set([initialRecipientId]));
    } else if (!isAdmin) {
      const president = Object.values(MOCK_USERS).find(u => u.role === UserRole.PRESIDENT);
      if (president) setSelectedIds(new Set([president.id]));
    }
  }, [initialRecipientId, isAdmin]);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      Array.from(files).forEach((file: File) => {
        const isVideo = file.type.startsWith('video/');
        const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
        const reader = new FileReader();
        reader.onload = () => {
          setAttachments(prev => [...prev, {
            url: reader.result as string,
            type: isVideo ? 'VIDEO' : isPdf ? 'DOCUMENT' : 'IMAGE',
            name: file.name
          }]);
        };
        reader.readAsDataURL(file);
      });
    }
  };

  const removeAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const filteredResidents = useMemo(() => {
    if (isAdmin) {
      return MOCK_RESIDENTS.filter(r => 
        r.tenantName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        r.unitNumber.toLowerCase().includes(searchQuery.toLowerCase())
      );
    } else {
      return Object.values(MOCK_USERS)
        .filter(u => u.role === UserRole.PRESIDENT || u.role === UserRole.DEPUTY)
        .map(u => ({
          id: u.id,
          tenantName: `${u.name} (${u.role === UserRole.PRESIDENT ? 'Претседател' : 'Заменик'})`,
          unitNumber: u.role === UserRole.PRESIDENT ? 'П' : 'З',
          type: UnitType.RESIDENTIAL,
          floor: '0'
        })) as any[];
    }
  }, [searchQuery, isAdmin]);

  const toggleSelection = (id: string) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
  };

  const selectAll = () => {
    setSelectedIds(new Set(MOCK_RESIDENTS.map(r => r.id)));
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
  };

  const selectFloor = (floor: string) => {
    const floorIds = MOCK_RESIDENTS.filter(r => r.floor === floor).map(r => r.id);
    const next = new Set(selectedIds);
    floorIds.forEach(id => next.add(id));
    setSelectedIds(next);
  };

  const handleSend = () => {
    if (selectedIds.size === 0 || !message) return;
    setIsSending(true);

    const recipientNames = isAdmin 
      ? MOCK_RESIDENTS.filter(r => selectedIds.has(r.id)).map(r => r.tenantName)
      : Object.values(MOCK_USERS).filter(u => selectedIds.has(u.id)).map(u => u.name);

    const newSentMsg: SentMessage = {
      id: `m-${Date.now()}`,
      timestamp: new Date().toISOString(),
      subject: subject || (isAdmin ? 'Без наслов' : 'Прашање до раководството'),
      content: message,
      recipientsCount: selectedIds.size,
      recipientNames: recipientNames.length > 0 ? recipientNames : ['Раководство'],
      type: selectedIds.size === 1 ? 'INDIVIDUAL' : 'GROUP',
      attachments: attachments.length > 0 ? attachments : undefined
    };

    setTimeout(() => {
      setSentHistory([newSentMsg, ...sentHistory]);
      setIsSending(false);
      setSubject('');
      setMessage('');
      setAttachments([]);
      if (isAdmin) setSelectedIds(new Set());
      setActiveView('HISTORY');
      onMessageSent();
    }, 1200);
  };

  const downloadPDF = (report: ReportTemplate) => {
    const content = `Извештај: ${report.title}\nДатум: ${new Date().toLocaleDateString()}\nЗграда: А1\n...\nCivicHome Digital Platform`;
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${report.id}_${new Date().toISOString().split('T')[0]}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const shareReport = (report: ReportTemplate) => {
    setSubject(`Официјален Извештај: ${report.title}`);
    setMessage(`Почитувани станари,\n\nВо прилог е најновиот извештај за: ${report.title}.\n\nПреглед:\n- Период: ${new Date().toLocaleString('mk-MK', { month: 'long', year: 'numeric' })}\n- Статус: Стабилно\n\nДеталите можете да ги погледнете во системот.`);
    setActiveView('NEW');
    setSelectedIds(new Set(MOCK_RESIDENTS.map(r => r.id)));
    setShowReportPreview(null);
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500 pb-12">
      <header className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight">
            {isAdmin ? 'Комуникациски центар' : 'Порака до раководството'}
          </h1>
          <p className="text-slate-500">
            {isAdmin 
              ? 'Директно информирање на станарите преку сите канали.' 
              : 'Испратете приватно прашање или барање до вашиот претседател.'}
          </p>
        </div>
        <div className="flex bg-white p-1 rounded-2xl border border-slate-200 shadow-sm overflow-x-auto">
          <button 
            onClick={() => setActiveView('NEW')}
            className={`px-4 py-2 rounded-xl text-sm font-bold whitespace-nowrap transition-all ${activeView === 'NEW' ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-100' : 'text-slate-500 hover:text-slate-800'}`}
          >
            {isAdmin ? 'Нова порака' : 'Пиши порака'}
          </button>
          {isAdmin && (
            <button 
              onClick={() => setActiveView('REPORTS')}
              className={`px-4 py-2 rounded-xl text-sm font-bold whitespace-nowrap transition-all ${activeView === 'REPORTS' ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-100' : 'text-slate-500 hover:text-slate-800'}`}
            >
              Извештаи
            </button>
          )}
          <button 
            onClick={() => setActiveView('HISTORY')}
            className={`px-4 py-2 rounded-xl text-sm font-bold whitespace-nowrap transition-all ${activeView === 'HISTORY' ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-100' : 'text-slate-500 hover:text-slate-800'}`}
          >
            {isAdmin ? 'Архива' : 'Мои пораки'}
          </button>
        </div>
      </header>

      {activeView === 'REPORTS' && isAdmin && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 animate-in slide-in-from-bottom-4 duration-500">
          {reportTemplates.map(report => (
            <div key={report.id} className="bg-white p-8 rounded-[2rem] border border-slate-200 shadow-sm hover:shadow-xl hover:border-indigo-200 transition-all group relative overflow-hidden">
               <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-50 rounded-full translate-x-16 -translate-y-16 group-hover:scale-110 transition-transform duration-500" />
               <div className="relative z-10">
                 <div className="w-12 h-12 bg-indigo-600 text-white rounded-2xl flex items-center justify-center mb-6 shadow-lg shadow-indigo-100">
                   {report.icon}
                 </div>
                 <h3 className="text-xl font-bold text-slate-900 mb-2">{report.title}</h3>
                 <p className="text-slate-500 text-sm mb-8 leading-relaxed">{report.description}</p>
                 <div className="flex flex-col space-y-2">
                   <button 
                    onClick={() => setShowReportPreview(report)}
                    className="w-full py-3 bg-slate-50 text-indigo-600 rounded-xl text-xs font-bold hover:bg-indigo-50 flex items-center justify-center border border-slate-100"
                   >
                     Преглед на податоци
                   </button>
                   <div className="grid grid-cols-2 gap-2">
                     <button 
                      onClick={() => downloadPDF(report)}
                      className="py-3 bg-white text-slate-700 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-slate-50 flex items-center justify-center border border-slate-200"
                     >
                       <Download className="w-3.5 h-3.5 mr-2" /> PDF
                     </button>
                     <button 
                      onClick={() => shareReport(report)}
                      className="py-3 bg-indigo-600 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-indigo-700 flex items-center justify-center shadow-lg shadow-indigo-100"
                     >
                       <Share2 className="w-3.5 h-3.5 mr-2" /> Сподели
                     </button>
                   </div>
                 </div>
               </div>
            </div>
          ))}
        </div>
      )}

      {activeView === 'NEW' && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className={`lg:col-span-5 space-y-4 ${!isAdmin ? 'hidden lg:block' : ''}`}>
            <div className="bg-white rounded-3xl border border-slate-200 overflow-hidden shadow-sm flex flex-col h-[600px]">
              <div className="p-6 border-b border-slate-50 bg-slate-50/50">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest">
                    {isAdmin ? 'Избери примачи' : 'Контакти за помош'}
                  </h3>
                  {isAdmin && (
                    <div className="flex items-center space-x-3">
                      <button 
                        onClick={selectAll}
                        className="text-[10px] font-black uppercase text-indigo-600 hover:text-indigo-800 transition-colors"
                      >
                        Избери сите
                      </button>
                      <span className="text-slate-300">|</span>
                      <button 
                        onClick={clearSelection}
                        className="text-[10px] font-black uppercase text-red-500 hover:text-red-700 transition-colors flex items-center"
                      >
                        <UserMinus className="w-3 h-3 mr-1" /> Откажи сите
                      </button>
                    </div>
                  )}
                </div>
                {isAdmin && (
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input 
                      type="text" 
                      placeholder="Пребарај станар..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 bg-white border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                    />
                  </div>
                )}
              </div>

              <div className="flex-1 overflow-y-auto p-2 space-y-1">
                {isAdmin && (
                  <div className="px-4 py-2 border-b border-slate-50 mb-2">
                    <span className="text-[9px] font-bold text-slate-400 uppercase block mb-2">Групи по кат</span>
                    <div className="flex flex-wrap gap-2">
                      {['1', '2', '3', '4'].map(f => (
                        <button 
                          key={f} 
                          onClick={() => selectFloor(f)}
                          className="px-3 py-1 bg-indigo-50 text-indigo-600 rounded-lg text-xs font-bold hover:bg-indigo-100 transition-colors"
                        >
                          Кат {f}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {filteredResidents.map((r: any) => (
                  <button 
                    key={r.id}
                    onClick={() => toggleSelection(r.id)}
                    className={`w-full flex items-center justify-between p-3 rounded-2xl transition-all ${
                      selectedIds.has(r.id) ? 'bg-indigo-50 border border-indigo-100' : 'hover:bg-slate-50 border border-transparent'
                    }`}
                  >
                    <div className="flex items-center space-x-3 text-left">
                      <div className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${
                        selectedIds.has(r.id) ? 'bg-indigo-600 border-indigo-600' : 'bg-white border-slate-300'
                      }`}>
                        {selectedIds.has(r.id) && <Check className="w-3.5 h-3.5 text-white stroke-[3px]" />}
                      </div>
                      
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-black ${
                        selectedIds.has(r.id) ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-500'
                      }`}>
                        {r.unitNumber}
                      </div>
                      <div>
                        <span className={`block text-sm font-bold ${selectedIds.has(r.id) ? 'text-indigo-900' : 'text-slate-800'}`}>
                          {r.tenantName}
                        </span>
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-tighter">
                          {isAdmin ? (r.type === UnitType.BUSINESS ? 'Деловен' : 'Станбен') : 'Раководство на зграда'}
                        </span>
                      </div>
                    </div>
                    {selectedIds.has(r.id) && <CheckCircle2 className="w-5 h-5 text-indigo-600" />}
                  </button>
                ))}
              </div>

              <div className={`p-6 bg-slate-900 text-white flex items-center justify-between ${!isAdmin ? 'bg-indigo-900' : ''}`}>
                <div>
                  <span className="text-[10px] font-black uppercase opacity-60 block">Примачи</span>
                  <span className="text-xl font-black">{selectedIds.size} лица</span>
                </div>
                {isAdmin ? <Users className="w-8 h-8 opacity-20" /> : <Shield className="w-8 h-8 opacity-20" />}
              </div>
            </div>
          </div>

          <div className="lg:col-span-7 space-y-6">
            <div className="bg-white rounded-3xl border border-slate-200 p-8 shadow-sm space-y-6">
              {!isAdmin && (
                <div className="bg-indigo-50 p-4 rounded-2xl border border-indigo-100 flex items-start space-x-3">
                   <Bell className="w-5 h-5 text-indigo-600 mt-1 flex-shrink-0" />
                   <p className="text-xs text-indigo-900 font-medium leading-relaxed">
                     Вашата порака ќе биде испратена директно до Претседателот и Заменикот. Тие ќе бидат известени веднаш.
                   </p>
                </div>
              )}
              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest">Тема / Наслов</label>
                <input 
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder={isAdmin ? "На пр. Известување за неплатени сметки" : "На пр. Проблем со паркингот"}
                  className="w-full p-4 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-indigo-500 outline-none font-bold"
                />
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest block mb-2">Порака</label>
                <textarea 
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Внесете ја содржината на пораката..."
                  className="w-full p-4 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-indigo-500 outline-none min-h-[200px] leading-relaxed"
                />
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest">Прилози</label>
                  <button 
                    onClick={() => fileInputRef.current?.click()}
                    className="text-xs font-bold text-slate-500 hover:text-indigo-600 flex items-center bg-slate-50 px-3 py-1.5 rounded-xl border border-slate-100 transition-all"
                  >
                    <Paperclip className="w-3.5 h-3.5 mr-2" /> Додај прилог
                  </button>
                  <input 
                    type="file" 
                    multiple 
                    ref={fileInputRef} 
                    className="hidden" 
                    accept="image/*,video/*,application/pdf"
                    onChange={handleFileUpload} 
                  />
                </div>

                {attachments.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {attachments.map((att, idx) => (
                      <div key={idx} className="relative group w-20 h-20 rounded-xl overflow-hidden border border-slate-200 bg-slate-50">
                        {att.type === 'IMAGE' ? (
                          <img src={att.url} className="w-full h-full object-cover" alt="Прилог" />
                        ) : att.type === 'VIDEO' ? (
                          <div className="w-full h-full flex items-center justify-center bg-indigo-50">
                            <Video className="w-6 h-6 text-indigo-400" />
                          </div>
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-red-50">
                            <FileText className="w-6 h-6 text-red-400" />
                          </div>
                        )}
                        <button 
                          onClick={() => removeAttachment(idx)}
                          className="absolute top-1 right-1 bg-red-600 text-white p-0.5 rounded-full opacity-0 group-hover:opacity-100 transition-all"
                        >
                          <X className="w-3 h-3" />
                        </button>
                        {att.type === 'DOCUMENT' && (
                          <div className="absolute bottom-0 inset-x-0 bg-white/90 p-1 text-[8px] font-bold truncate">
                            {att.name}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {isAdmin && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="flex items-center p-3 bg-slate-50 rounded-xl border border-slate-100">
                    <Mail className="w-4 h-4 text-indigo-500 mr-2" />
                    <span className="text-xs font-bold text-slate-600">Е-пошта</span>
                  </div>
                  <div className="flex items-center p-3 bg-slate-50 rounded-xl border border-slate-100">
                    <Smartphone className="w-4 h-4 text-indigo-500 mr-2" />
                    <span className="text-xs font-bold text-slate-600">СМС / Вибер</span>
                  </div>
                  <div className="flex items-center p-3 bg-slate-50 rounded-xl border border-slate-100">
                    <Bell className="w-4 h-4 text-indigo-500 mr-2" />
                    <span className="text-xs font-bold text-slate-600">Ин-Ап Нотификација</span>
                  </div>
                </div>
              )}

              <button 
                onClick={handleSend}
                disabled={selectedIds.size === 0 || !message || isSending}
                className={`w-full py-4 rounded-[1.5rem] font-black text-lg transition-all flex items-center justify-center shadow-xl ${
                  selectedIds.size === 0 || !message ? 'bg-slate-100 text-slate-400' : 'bg-indigo-600 text-white shadow-indigo-100 hover:bg-indigo-700 active:scale-95'
                }`}
              >
                {isSending ? (
                  <><Loader2 className="w-5 h-5 animate-spin mr-3" /> Се испраќа...</>
                ) : (
                  <><Send className="w-5 h-5 mr-3" /> {isAdmin ? `Испрати до ${selectedIds.size} корисници` : 'Испрати до раководство'}</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {activeView === 'HISTORY' && (
        <div className="bg-white rounded-[2.5rem] border border-slate-200 overflow-hidden shadow-sm animate-in fade-in duration-500">
          <div className="p-8 bg-slate-50/50 border-b border-slate-100 flex items-center justify-between">
            <h3 className="text-sm font-black text-slate-900 uppercase tracking-widest">
              {isAdmin ? 'Архива на испратени пораки' : 'Моја комуникација'}
            </h3>
            <span className="text-xs font-bold text-slate-400">{sentHistory.length} вкупно</span>
          </div>
          {sentHistory.length > 0 ? (
            <div className="divide-y divide-slate-100">
              {sentHistory.map(msg => (
                <div key={msg.id} className="p-8 hover:bg-slate-50 transition-colors group">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <span className={`px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-widest border ${
                        msg.type === 'ALL' ? 'bg-indigo-50 text-indigo-600 border-indigo-100' : 
                        msg.type === 'GROUP' ? 'bg-amber-50 text-amber-600 border-amber-100' : 'bg-emerald-50 text-emerald-600 border-emerald-100'
                      }`}>
                        {msg.type === 'ALL' ? 'Сите' : msg.type === 'GROUP' ? 'Група' : 'Индивидуално'}
                      </span>
                      <span className="text-xs font-bold text-slate-400">{new Date(msg.timestamp).toLocaleString('mk-MK')}</span>
                    </div>
                    <div className="flex items-center text-[10px] font-bold text-emerald-500 uppercase">
                      <Check className="w-3 h-3 mr-1" /> Доставено
                    </div>
                  </div>
                  <h4 className="text-xl font-bold text-slate-900 group-hover:text-indigo-600 transition-colors mb-2">{msg.subject}</h4>
                  <p className="text-sm text-slate-600 leading-relaxed font-medium mb-4">{msg.content}</p>
                  
                  {msg.attachments && msg.attachments.length > 0 && (
                    <div className="flex flex-wrap gap-3 mb-4">
                      {msg.attachments.map((att, i) => (
                        <div key={i} className="relative group/att w-24 h-24 rounded-2xl overflow-hidden border border-slate-100 bg-white shadow-sm cursor-pointer hover:border-indigo-300 transition-all">
                          {att.type === 'IMAGE' ? (
                            <img src={att.url} className="w-full h-full object-cover" alt="Прилог" />
                          ) : att.type === 'VIDEO' ? (
                            <div className="w-full h-full flex items-center justify-center bg-slate-50">
                              <Play className="w-8 h-8 text-indigo-600 fill-indigo-600" />
                            </div>
                          ) : (
                            <div className="w-full h-full flex flex-col items-center justify-center bg-red-50/50 p-2">
                              <FileText className="w-8 h-8 text-red-500 mb-1" />
                              <span className="text-[8px] font-black text-red-700 text-center truncate w-full">
                                {att.name || 'Документ'}
                              </span>
                            </div>
                          )}
                          <a 
                            href={att.url} 
                            download={att.name || 'прилог'} 
                            className="absolute inset-0 bg-black/40 opacity-0 group-hover/att:opacity-100 flex items-center justify-center transition-all"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Download className="w-6 h-6 text-white" />
                          </a>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="flex items-center flex-wrap gap-2 pt-4 border-t border-slate-50">
                    <span className="text-[10px] font-black text-slate-300 uppercase tracking-tighter">
                      {isAdmin ? 'Примачи:' : 'До:'}
                    </span>
                    {msg.recipientNames.slice(0, 5).map((n, i) => (
                      <span key={i} className="text-[10px] font-bold text-slate-500 bg-white px-2 py-0.5 rounded border border-slate-100">
                        {n}
                      </span>
                    ))}
                    {msg.recipientsCount > 5 && (
                      <span className="text-[10px] font-bold text-slate-400">+{msg.recipientsCount - 5} други</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-32">
              <div className="w-20 h-20 bg-slate-50 text-slate-300 rounded-full flex items-center justify-center mx-auto mb-6">
                <History className="w-10 h-10" />
              </div>
              <p className="text-slate-400 font-bold uppercase text-xs tracking-widest">Нема историја на пораки.</p>
            </div>
          )}
        </div>
      )}

      {showReportPreview && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-md" onClick={() => setShowReportPreview(null)} />
          <div className="relative bg-white w-full max-w-2xl rounded-[2.5rem] shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200 border border-indigo-100">
             <header className="p-8 bg-indigo-600 text-white flex justify-between items-start">
               <div>
                 <h2 className="text-2xl font-black">Извештај: {showReportPreview.title}</h2>
                 <p className="text-indigo-100 text-xs font-bold uppercase tracking-widest mt-1">Зграда А1 • Скопје • {new Date().toLocaleDateString('mk-MK')}</p>
               </div>
               <button onClick={() => setShowReportPreview(null)} className="p-2 bg-white/20 rounded-full hover:bg-white/30 transition-colors">
                 <X className="w-6 h-6" />
               </button>
             </header>

             <div className="p-8 space-y-8 max-h-[60vh] overflow-y-auto bg-slate-50/30">
                {showReportPreview.category === 'MAINTENANCE' && (
                  <div className="grid grid-cols-3 gap-4">
                     <div className="bg-white p-5 rounded-3xl border border-slate-100 shadow-sm text-center">
                        <span className="block text-[10px] font-bold text-slate-400 uppercase mb-2">Активни</span>
                        <span className="text-3xl font-black text-amber-500">{MOCK_TICKETS.filter(t => t.status !== 'RESOLVED').length}</span>
                     </div>
                     <div className="bg-white p-5 rounded-3xl border border-slate-100 shadow-sm text-center">
                        <span className="block text-[10px] font-bold text-slate-400 uppercase mb-2">Решени</span>
                        <span className="text-3xl font-black text-emerald-500">{MOCK_TICKETS.filter(t => t.status === 'RESOLVED').length}</span>
                     </div>
                     <div className="bg-white p-5 rounded-3xl border border-slate-100 shadow-sm text-center">
                        <span className="block text-[10px] font-bold text-slate-400 uppercase mb-2">Ефикасност</span>
                        <span className="text-3xl font-black text-indigo-600">84%</span>
                     </div>
                  </div>
                )}
                
                {showReportPreview.category === 'FINANCE' && (
                  <div className="space-y-4">
                     <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm">
                        <div className="flex items-center justify-between mb-4">
                           <span className="text-sm font-bold text-slate-900">Наплата на резервен фонд</span>
                           <span className="text-sm font-black text-emerald-600">92%</span>
                        </div>
                        <div className="w-full h-3 bg-slate-100 rounded-full overflow-hidden">
                           <div className="h-full bg-emerald-500" style={{ width: '92%' }} />
                        </div>
                     </div>
                     <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm">
                        <div className="flex items-center justify-between mb-4">
                           <span className="text-sm font-bold text-slate-900">Наплата на хигиена</span>
                           <span className="text-sm font-black text-amber-500">76%</span>
                        </div>
                        <div className="w-full h-3 bg-slate-100 rounded-full overflow-hidden">
                           <div className="h-full bg-amber-500" style={{ width: '76%' }} />
                        </div>
                     </div>
                  </div>
                )}

                <div className="bg-white p-6 rounded-3xl border border-slate-200 shadow-inner">
                   <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-4">Опис и препораки</h4>
                   <p className="text-sm text-slate-700 leading-relaxed font-medium">
                      Податоците покажуваат стабилен тренд во тековниот месец. Потребно е да се обрне внимание на единиците кои доцнат со уплата повеќе од 3 месеци. Сервисирањето на лифтот е успешно завршено.
                   </p>
                </div>
             </div>

             <footer className="p-8 bg-white border-t flex space-x-4">
                <button 
                  onClick={() => downloadPDF(reportTemplates[0])}
                  className="flex-1 py-4 bg-slate-100 text-slate-700 rounded-2xl font-bold hover:bg-slate-200 transition-all flex items-center justify-center border border-slate-200"
                >
                  <Download className="w-5 h-5 mr-2" /> PDF Извештај
                </button>
                <button 
                  onClick={() => shareReport(reportTemplates[0])}
                  className="flex-1 py-4 bg-indigo-600 text-white rounded-2xl font-bold hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-100 flex items-center justify-center"
                >
                  <Share2 className="w-5 h-5 mr-2" /> Сподели со сите
                </button>
             </footer>
          </div>
        </div>
      )}
    </div>
  );
};

export default Communication;
